import { useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { BrandLogoFull, BrandLogo } from "@/components/BrandLogo";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
      toast.success("If that email is registered, a reset link is on the way.");
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="forgot-password-page">
      <div className="hidden lg:flex lg:w-1/2 bg-zinc-950 text-white relative overflow-hidden bwz-grid-bg">
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 opacity-90" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <BrandLogoFull size="md" testId="forgot-brand" />
            <div>
              <div className="font-display font-bold text-xl">Brandon Wheelz</div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mt-0.5">OPS Central</div>
            </div>
          </div>
          <div className="space-y-6 max-w-md">
            <h1 className="font-display text-4xl xl:text-5xl font-bold tracking-tight">
              Locked out? We'll get you back in.
            </h1>
            <p className="text-zinc-400 text-base leading-relaxed">
              Enter the email tied to your operations account. If it's registered, we'll email you a one-time link to reset your password.
            </p>
          </div>
          <div className="text-xs text-zinc-500 font-mono">v1.0 • internal use</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-zinc-50">
        <Card className="w-full max-w-md p-8 border-zinc-200 shadow-sm" data-testid="forgot-card">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <BrandLogo to={null} size="sm" testId="forgot-mobile-brand" />
            <div>
              <div className="font-display font-bold">Brandon Wheelz</div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">OPS Central</div>
            </div>
          </div>

          {!sent ? (
            <>
              <h2 className="font-display text-2xl font-semibold tracking-tight">Forgot password</h2>
              <p className="text-sm text-zinc-500 mt-1 mb-6">We'll email you a secure link to reset it.</p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider text-zinc-600">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-email-input"
                    className="mt-1.5"
                    placeholder="you@brandonwheelz.com"
                  />
                </div>
                {error && <div className="text-xs text-red-600" data-testid="forgot-error">{error}</div>}
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="forgot-submit-button"
                  className="w-full bg-zinc-950 hover:bg-zinc-800 text-white"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Send reset link
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-4" data-testid="forgot-sent">
              <div className="mx-auto w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </div>
              <h2 className="font-display text-xl font-semibold tracking-tight">Check your inbox</h2>
              <p className="text-sm text-zinc-500 mt-2">
                If <span className="font-mono text-zinc-700">{email}</span> is registered, you'll receive an email with a link to reset your password. The link is valid for 30 minutes.
              </p>
              <p className="text-xs text-zinc-400 mt-4">
                Didn't get it? Check spam, or try again in a few minutes.
              </p>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-zinc-100">
            <Link
              to="/login"
              data-testid="back-to-login-link"
              className="inline-flex items-center gap-1.5 text-xs text-zinc-600 hover:text-zinc-950 transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to sign in
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
